import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { ReactNode } from 'react';

interface SlideShellProps {
  children: ReactNode;
  lessonTitle?: string;
  lessonThumbnail?: string;
  slideNumber: number;
  totalSlides: number;
  subtitle?: string;
  onBackToLesson?: () => void;
  onBack?: () => void;
  onNext?: () => void;
  showMarkComplete?: boolean;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const SlideShell = ({
  children,
  lessonTitle,
  lessonThumbnail,
  slideNumber,
  totalSlides,
  subtitle,
  onBackToLesson,
  onBack,
  onNext,
  showMarkComplete,
  isCompleted,
  onMarkComplete,
}: SlideShellProps) => {
  return (
    <div className="h-full flex flex-col bg-[#f8f8f7]">
      {/* Sticky Header */}
      {lessonTitle && (
        <div className="sticky top-0 z-20 bg-[#f8f8f7] border-b border-[#e5e5e5] px-[6%] py-4">
          <button
            onClick={onBackToLesson}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-[#27c840] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back to Lesson</span>
            {lessonThumbnail && (
              <img src={lessonThumbnail} alt="" className="w-6 h-6 rounded-md object-cover ml-1" />
            )}
            <span className="text-muted-foreground">· {lessonTitle}</span>
          </button>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 px-[6%] pb-8 overflow-y-auto">
        <div className="max-w-3xl mx-auto pt-8">
          {children}
        </div>
      </div>

      {/* Bottom Navigation - Full Width */}
      <div className="w-full bg-[#f8f8f7] border-t border-[#e5e5e5] h-[63px] shadow-[0_-2px_8px_rgba(0,0,0,0.03)]">
        <div className="h-full px-[6%] flex items-center justify-between">
          {/* Left: Back */}
          <Button
            variant="outline"
            onClick={onBack}
            disabled={!onBack}
            className="gap-2 rounded-[12px]"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          {/* Center: Progress */}
          <div className="text-xs text-muted-foreground">
            {slideNumber} of {totalSlides}
          </div>
          
          {/* Right: Skip + Mark Complete / Completed + Next */}
          <div className="flex items-center gap-3">
            {!isCompleted && onNext && showMarkComplete && (
              <button
                onClick={onNext}
                className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
              >
                Skip
              </button>
            )}
            
            {showMarkComplete && onMarkComplete && (
              <Button
                onClick={onMarkComplete}
                disabled={isCompleted}
                className={`gap-2 rounded-[12px] ${
                  isCompleted 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-[#27c840] hover:bg-[#22b038] text-white'
                }`}
              >
                {isCompleted ? (
                  <>
                    <Check className="w-4 h-4" />
                    Completed
                  </>
                ) : (
                  'Mark Complete'
                )}
              </Button>
            )}
            
            {isCompleted && onNext && (
              <Button
                onClick={onNext}
                className="gap-2 bg-[#27c840] hover:bg-[#22b038] text-white rounded-[12px]"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideShell;
